import React, { useMemo } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Grid, Button, IconButton, Typography, Box, Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { colors } from "../../../Shared/colors";

/**
 * Edit an existing staff member.
 *
 * Rebuilt to match the Add Staff dialog. The two forms edit the same record and
 * were built two different ways — this one was a hand-rolled popup with raw
 * inputs and `em`-based spacing, so every gap scaled with its own font size and
 * nothing lined up between rows. Same fields, same order, same controls now, on
 * a fixed 8px scale.
 *
 * Password is the one deliberate difference: on create it is required, here it
 * is optional and only sent when filled, so saving a name change does not
 * silently reset someone's login.
 */

const DESIGNATIONS = [
  "dos", "doa", "account", "admin", "dm", "rsm", "sm", "manger",
  "abdm", "bdm", "bda", "am", "dcc", "telesale", "developer", "Hr", "aps",
];

const ROLES = [
  "dos", "doa", "account", "admin", "dm", "rsm", "sm", "manger", "abdm",
  "bdm", "bda", "am", "dcc", "ts", "developer", "v3", "Hr", "aps",
];

const REQUIRED = [
  ["firstName", "First name"], ["lastName", "Last name"],
  ["email", "Email"], ["phoneNum", "Phone number"],
  ["cnic", "CNIC"], ["address", "Address"],
  ["gender", "Gender"], ["designation", "Designation"],
];

/** One place for the field props every input repeats. */
const fieldProps = {
  fullWidth: true,
  size: "small",
  InputLabelProps: { shrink: true },
};

const selectProps = {
  ...fieldProps,
  select: true,
  SelectProps: {
    displayEmpty: true,
    // Without this the page shifts left by the scrollbar width each time a
    // menu opens, because MUI locks body scroll and pads to compensate.
    MenuProps: { disableScrollLock: true },
  },
};

export const EditStaffDialog = ({
  open,
  staffData = {},
  region = [],
  isLoading,
  onChange,
  onProfilePicChange,
  onSave,
  onClose,
  fileInputRef,
  password,
  onPasswordChange,
}) => {
  const missing = useMemo(
    () => REQUIRED.filter(([k]) => !String(staffData?.[k] ?? "").trim()),
    [staffData]
  );
  const complete = missing.length === 0;

  const fullName = `${staffData?.firstName || ""} ${staffData?.lastName || ""}`.trim();

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: "16px" },
          m: { xs: 0, sm: 4 },
          width: { xs: "100%", sm: "auto" },
          maxHeight: { xs: "100%", sm: "calc(100% - 64px)" },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          borderBottom: `1px solid ${colors.inputBorderWhite}`,
          pb: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" fontWeight="bold" color={colors.text} noWrap>
            Edit {fullName || "Staff Member"}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.textLight }}>
            Leave the password blank to keep the current one
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Section>Personal Information</Section>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField {...fieldProps} required label="First Name" name="firstName"
              id="edit-firstName" placeholder="Ahmed"
              value={staffData.firstName || ""} onChange={onChange} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField {...fieldProps} required label="Last Name" name="lastName"
              id="edit-lastName" placeholder="Raza"
              value={staffData.lastName || ""} onChange={onChange} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField {...fieldProps} required type="email" label="Email" name="email"
              id="edit-email" placeholder="ahmed.raza@jagiir.com"
              value={staffData.email || ""} onChange={onChange} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField {...fieldProps} type="password" label="New Password"
              id="edit-password" placeholder="Leave blank to keep current"
              helperText="Only sent if you fill it in"
              value={password || ""} onChange={onPasswordChange} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField {...fieldProps} required type="tel" label="Phone Number" name="phoneNum"
              id="edit-phoneNum" placeholder="03001234567" helperText="11 digits, no dashes"
              value={staffData.phoneNum || ""} onChange={onChange} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField {...fieldProps} required label="CNIC" name="cnic" inputMode="numeric"
              id="edit-cnic" placeholder="3520112345671" helperText="13 digits, no dashes"
              value={staffData.cnic || ""} onChange={onChange} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField {...fieldProps} required label="Address" name="address"
              id="edit-address" placeholder="House 12, Gulberg III, Lahore"
              value={staffData.address || ""} onChange={onChange} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField {...selectProps} required label="Gender" name="gender"
              id="edit-gender" value={staffData.gender || ""} onChange={onChange}>
              <MenuItem value=""><em>Select</em></MenuItem>
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
          </Grid>
        </Grid>

        <Section sx={{ mt: 3.5 }}>Job Details</Section>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField {...fieldProps} label="Employee ID" name="empID"
              id="edit-empID" placeholder="EMP-1042"
              value={staffData.empID || ""} onChange={onChange} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField {...selectProps} required label="Designation" name="designation"
              id="edit-designation" value={staffData.designation || ""} onChange={onChange}>
              <MenuItem value=""><em>Select</em></MenuItem>
              {DESIGNATIONS.map((d) => (
                <MenuItem key={d} value={d}>{d.toUpperCase()}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField {...selectProps} label="Role" name="role"
              id="edit-role" value={staffData.role || ""} onChange={onChange}>
              <MenuItem value=""><em>Select</em></MenuItem>
              {ROLES.map((r) => (
                <MenuItem key={r} value={r}>{r.toUpperCase()}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField {...selectProps} label="Region" name="region"
              id="edit-region"
              value={staffData.region?._id || staffData.region || ""} onChange={onChange}>
              <MenuItem value=""><em>Select</em></MenuItem>
              {region?.map((r) => (
                <MenuItem key={r._id} value={r._id}>{r.regionName}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField {...fieldProps} label="Assign Area" name="assignArea"
              id="edit-assignArea" placeholder="Gulberg, Model Town"
              value={staffData.assignArea || ""} onChange={onChange} />
          </Grid>
        </Grid>

        <Section sx={{ mt: 3.5 }}>Profile Picture</Section>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", pb: 1 }}>
          <Avatar
            src={
              staffData.profilePic instanceof File
                ? URL.createObjectURL(staffData.profilePic)
                : staffData.profilePic?.url || staffData.profilePic || undefined
            }
            alt={fullName ? `${fullName}'s profile picture` : "Profile picture"}
            sx={{ width: 56, height: 56, bgcolor: colors.inputWhite }}
          />
          <Button
            component="label"
            variant="outlined"
            startIcon={<AttachFileIcon />}
            sx={{
              textTransform: "none", fontWeight: 600, borderRadius: "8px",
              minHeight: 40, color: colors.text, borderColor: colors.inputBorderWhite,
            }}
          >
            {staffData.profilePic ? "Replace image" : "Upload image"}
            <input
              hidden
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={onProfilePicChange}
            />
          </Button>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: { xs: 2, sm: 3 },
          borderTop: `1px solid ${colors.inputBorderWhite}`,
          position: "sticky",
          bottom: 0,
          backgroundColor: colors.white,
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="caption"
          role="status"
          sx={{ color: complete ? "#1B7F3B" : colors.textLight, fontWeight: 600, mr: "auto" }}
        >
          {complete
            ? "Ready to save"
            : `Still needed: ${missing.map(([, l]) => l).join(", ")}`}
        </Typography>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: "8px", minHeight: 40, textTransform: "none", fontWeight: 600,
            color: colors.text, borderColor: colors.inputBorderWhite,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={isLoading || !complete}
          sx={{
            borderRadius: "8px", minHeight: 40, minWidth: 130,
            textTransform: "none", fontWeight: 600,
            backgroundColor: colors.brown,
            "&:hover": { backgroundColor: "#1a6b6b" },
          }}
        >
          {isLoading ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Section heading. One consistent rhythm instead of per-field margins. */
const Section = ({ children, sx }) => (
  <Typography
    variant="subtitle2"
    sx={{
      fontWeight: 700,
      color: colors.brown,
      mb: 1.5,
      ...sx,
    }}
  >
    {children}
  </Typography>
);
